import React, { useRef } from "react";
import {
  AHIcon,
  AHTeamIconCircle,
  AHText,
  Box,
  SpaceProps,
  TouchableBox,
  useLayoutContext,
} from "arkh";
import { NumberGoalValueInput } from "../../../../components/mobile/NumberGoalValueInput";
import { TextInput } from "react-native-gesture-handler";
import { useActionSheet } from "@expo/react-native-action-sheet";
import { Platform } from "react-native";
import { LegacyListItem } from "../../components/ListItem";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { useIntl } from "react-intl";
import { MoreButton } from "../../../../common/MoreButton";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export function KPIEditSectionsListItem(
  props: {
    name: string;
    team?: {
      icon: string | null;
      color: string | null;
    };
    goalDirection: "greater" | "less";
    goalValue: number;
    setGoalValue?: (value: number) => void;
    onRemove?: () => void;
    dynamicHeight?: boolean;
    onAddSubsection?: () => void;
    onEditSubsection?: () => void;
    isFirst?: boolean;
    isLast?: boolean;
  } & SpaceProps
) {
  const ref = useRef<TextInput>(null);
  const { showActionSheetWithOptions } = useActionSheet();
  const anchor = useRef<undefined | number>(undefined);
  const intl = useIntl();
  const intl_tokens = useIntlTokens();
  const insets = useSafeAreaInsets();

  const onMore = () => {
    if (!props.team) {
      props.onEditSubsection?.();
      return;
    }
    if (!props.onRemove) {
      return;
    }
    const removeMeasurementArea = intl.formatMessage({
      defaultMessage: "Remove measurement area",
      description:
        "KPIEditSectionListItem more option: remove measurement area",
    });
    showActionSheetWithOptions(
      {
        options: props.team
          ? [
              intl_tokens.Cancel,
              intl.formatMessage({
                defaultMessage: "Add measurement area",
                description:
                  "KPIEditSectionListItem more option: add measurement area",
              }),
              removeMeasurementArea,
            ]
          : [
              intl_tokens.Cancel,
              intl.formatMessage({
                defaultMessage: "Change team",
                description: "KPIEditSectionListItem more option: change team",
              }),
              removeMeasurementArea,
            ],
        cancelButtonIndex: 0,
        destructiveButtonIndex: 2,
        anchor: anchor.current ?? undefined,
        containerStyle:
          Platform.OS === "android"
            ? {
                paddingBottom: insets.bottom,
              }
            : undefined,
      },
      (i) => {
        switch (i) {
          case 1: {
            if (props.team) {
              props.onAddSubsection?.();
            } else {
              props.onEditSubsection?.();
            }
            break;
          }
          case 2: {
            props.onRemove?.();
            break;
          }
        }
      }
    );
  };
  const layout = useLayoutContext();

  return (
    <LegacyListItem
      style={{
        flexDirection: "row",
        alignItems: "center",
        minHeight: 0,
      }}
      isFirst={props.isFirst}
      isLast={props.isLast}
      dynamicHeight={props.dynamicHeight}
      disabled={!props.setGoalValue}
      onPress={() => {
        ref.current?.focus();
      }}
      space={props.space}
      spaceVertical={props.spaceVertical}
      spaceHorizontal={props.spaceHorizontal ?? "none"}
      spaceLeft={props.spaceLeft}
      spaceRight={props.spaceRight}
      spaceTop={props.spaceTop}
      spaceBottom={props.spaceBottom}
    >
      {props.team ? (
        <Box
          spaceRight="small"
          style={{ flexDirection: "row", alignItems: "center" }}
        >
          <AHTeamIconCircle
            color={props.team.color}
            type={props.team.icon as any}
            size="smallMedium"
          />
        </Box>
      ) : null}
      <AHText type="list-item-title" style={{ flex: 1 }}>
        {props.name}
      </AHText>
      {props.setGoalValue ? (
        <NumberGoalValueInput
          ref={ref}
          goal={props.goalDirection === "greater" ? "min" : "max"}
          value={props.goalValue}
          setValue={props.setGoalValue}
          size="small"
        />
      ) : null}
      {props.onRemove || props.onEditSubsection || props.onAddSubsection ? (
        Platform.OS === "web" ? (
          <MoreButton
            items={[
              props.team
                ? {
                    label: intl.formatMessage({
                      defaultMessage: "Add measurement area",
                      description:
                        "KPIEditSectionListItem more option: add measurement area",
                    }),
                    onPress: props.onAddSubsection ?? (() => {}),
                  }
                : {
                    label: intl.formatMessage({
                      defaultMessage: "Change team",
                      description:
                        "KPIEditSectionListItem more option: change team",
                    }),
                    onPress: props.onEditSubsection ?? (() => {}),
                  },
              ...(props.onRemove
                ? [
                    {
                      label: intl.formatMessage({
                        defaultMessage: "Remove measurement area",
                        description:
                          "KPIEditSectionListItem more option: remove measurement area",
                      }),
                      onPress: props.onRemove,
                    },
                  ]
                : []),
            ]}
          >
            {(open) => (
              <TouchableBox
                hitSlop={{ top: 20, bottom: 20, left: 20, right: 90 }}
                onPress={open}
                style={{ marginLeft: layout.space.medium }}
              >
                <AHIcon type="more" color="darkText" size="medium" />
              </TouchableBox>
            )}
          </MoreButton>
        ) : (
          <TouchableBox
            ref={anchor as any}
            hitSlop={{ top: 20, bottom: 20, left: 20, right: 90 }}
            onPress={onMore}
            style={{
              marginLeft: layout.space.medium,
            }}
          >
            <AHIcon type="more" color="darkerText" size="medium" />
          </TouchableBox>
        )
      ) : null}
    </LegacyListItem>
  );
}
